import React from 'react'
import { AdCreative, AdCreativeType } from '@marketing-milk/interfaces'
import { formatCallToAction } from './formatCallToAction'

export interface LocalAdPreviewProps {
  creative: AdCreative
  /** Facebook page name shown in the header; falls back to a neutral label. */
  pageName?: string
  /** Facebook page avatar; falls back to an initials chip when absent. */
  pageImageURL?: string
}

/** The local fallback faithfully renders only these formats (carousel/dynamic get a placeholder). */
export const isLocallyRenderable = (format: AdCreativeType) =>
  format === AdCreativeType.single_image || format === AdCreativeType.single_video

/** FB feed truncates the primary text and shows "… See more"; ~125 chars is the rule of thumb. */
const PRIMARY_TEXT_LIMIT = 125

const colors = {
  card: '#ffffff',
  border: '#dadde1',
  ink: '#050505',
  muted: '#65676b',
  faint: '#8a8d91',
  linkStrip: '#f0f2f5',
  ctaBg: '#e4e6eb',
  ctaInk: '#050505',
  avatarBg: '#c4cdd6',
}

function initials(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map(w => w[0]?.toUpperCase() ?? '')
    .join('')
}

function PageHeader({ pageName, pageImageURL }: { pageName: string; pageImageURL?: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 12px 8px' }}>
      {pageImageURL ? (
        <img
          src={pageImageURL}
          alt=""
          style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
        />
      ) : (
        <div
          aria-hidden
          style={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            background: colors.avatarBg,
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 15,
            fontWeight: 600,
            flexShrink: 0,
          }}
        >
          {initials(pageName)}
        </div>
      )}
      <div style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: 14, color: colors.ink, lineHeight: 1.2 }}>
          {pageName}
        </div>
        <div style={{ fontSize: 12, color: colors.faint, lineHeight: 1.4 }}>Sponsored · 🌐</div>
      </div>
    </div>
  )
}

function PrimaryText({ text }: { text: string }) {
  const [expanded, setExpanded] = React.useState(false)
  const truncatable = text.length > PRIMARY_TEXT_LIMIT
  const shown = truncatable && !expanded ? `${text.slice(0, PRIMARY_TEXT_LIMIT).trimEnd()}…` : text
  return (
    <div
      style={{
        padding: '0 12px 10px',
        fontSize: 14,
        lineHeight: 1.4,
        color: colors.ink,
        whiteSpace: 'pre-wrap',
      }}
    >
      {shown}
      {truncatable && (
        <>
          {' '}
          <button
            type="button"
            onClick={() => setExpanded(e => !e)}
            style={{
              border: 'none',
              background: 'transparent',
              padding: 0,
              font: 'inherit',
              color: colors.muted,
              cursor: 'pointer',
            }}
          >
            {expanded ? 'See less' : 'See more'}
          </button>
        </>
      )}
    </div>
  )
}

function ImageMedia({ url }: { url?: string }) {
  if (!url) {
    return (
      <div
        style={{
          width: '100%',
          aspectRatio: '1 / 1',
          background: colors.linkStrip,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: colors.faint,
          fontSize: 13,
        }}
      >
        No image
      </div>
    )
  }
  return (
    <img
      src={url}
      alt=""
      style={{ width: '100%', display: 'block', aspectRatio: '1 / 1', objectFit: 'cover' }}
    />
  )
}

function VideoUnavailable() {
  return (
    <div
      style={{
        width: '100%',
        aspectRatio: '1 / 1',
        background: colors.linkStrip,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: colors.faint,
        fontSize: 13,
      }}
    >
      Video unavailable
    </div>
  )
}

/**
 * Native single-video playback for review. Prefers our durable stored copy
 * (`storedSource`) over the expiring Facebook `source`. If the video fails to
 * load, falls back to the poster image, then to a clear unavailable state — never
 * a broken <video> or <img>.
 */
function VideoMedia({ source, poster }: { source?: string; poster?: string }) {
  const [errored, setErrored] = React.useState(false)

  if (source && !errored) {
    return (
      <video
        src={source}
        poster={poster}
        controls
        playsInline
        preload="metadata"
        onError={() => setErrored(true)}
        style={{
          width: '100%',
          display: 'block',
          aspectRatio: '1 / 1',
          objectFit: 'cover',
          background: '#000',
        }}
      />
    )
  }
  if (poster) {
    return <ImageMedia url={poster} />
  }
  return <VideoUnavailable />
}

function LinkStrip({ creative }: { creative: AdCreative }) {
  const { linkDisplayURL, linkHeadline, linkDescription, callToAction } = creative
  if (!linkDisplayURL && !linkHeadline && !linkDescription && !callToAction) return null
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '10px 12px',
        background: colors.linkStrip,
      }}
    >
      <div style={{ minWidth: 0, flex: 1 }}>
        {linkDisplayURL && (
          <div
            style={{
              fontSize: 12,
              letterSpacing: 0.3,
              textTransform: 'uppercase',
              color: colors.faint,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {linkDisplayURL}
          </div>
        )}
        {linkHeadline && (
          <div style={{ fontSize: 15, fontWeight: 600, color: colors.ink, lineHeight: 1.3 }}>
            {linkHeadline}
          </div>
        )}
        {linkDescription && (
          <div style={{ fontSize: 13, color: colors.muted, lineHeight: 1.3 }}>
            {linkDescription}
          </div>
        )}
      </div>
      {callToAction && (
        <button
          type="button"
          disabled
          style={{
            flexShrink: 0,
            border: 'none',
            borderRadius: 6,
            background: colors.ctaBg,
            color: colors.ctaInk,
            fontSize: 14,
            fontWeight: 600,
            padding: '8px 12px',
            cursor: 'default',
            whiteSpace: 'nowrap',
          }}
        >
          {formatCallToAction(callToAction)}
        </button>
      )}
    </div>
  )
}

function Placeholder({ creative }: { creative: AdCreative }) {
  const label =
    creative.format === AdCreativeType.carousel
      ? `Carousel · ${creative.carouselSlides?.length ?? 0} cards`
      : 'Dynamic creative'
  return (
    <div
      style={{
        padding: '28px 16px',
        textAlign: 'center',
        color: colors.muted,
        fontSize: 13,
        lineHeight: 1.5,
      }}
    >
      <div style={{ fontWeight: 600, color: colors.ink, marginBottom: 4 }}>{label}</div>
      Live Facebook preview unavailable — open in Facebook to review this format.
    </div>
  )
}

/**
 * A Facebook-independent, network-free approximation of the FB mobile-feed ad,
 * drawn entirely from fields already hydrated on the creative (no preview fetch).
 * Used as the instant base layer beneath the live FB iframe (see `MobilePreview`'s
 * `fallback`): it paints immediately and stays visible whenever the live FB
 * preview is slow, down, or token-expired, so an AE can still review and approve
 * the ad's copy, image, headline, display URL, and CTA.
 *
 * Faithful for `single_image` / `single_video`; `carousel` / `dynamic` render a
 * minimal informative placeholder (not a faked layout), and callers must not treat
 * the placeholder as an authoritative surface for approval.
 */
export function LocalAdPreview({ creative, pageName, pageImageURL }: LocalAdPreviewProps) {
  const name = pageName?.trim() || 'Your Page'
  const isImage = creative.format === AdCreativeType.single_image
  const isVideo = creative.format === AdCreativeType.single_video

  return (
    <div
      style={{
        width: '100%',
        background: colors.card,
        border: `1px solid ${colors.border}`,
        borderRadius: 8,
        overflow: 'hidden',
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
        textAlign: 'left',
      }}
    >
      <PageHeader pageName={name} pageImageURL={pageImageURL} />
      {creative.primaryText && <PrimaryText text={creative.primaryText} />}
      {isImage && (
        <ImageMedia url={creative.singleImage?.url ?? creative.imageURL ?? creative.thumbnailURL} />
      )}
      {isVideo && (
        <VideoMedia
          source={creative.singleVideo?.storedSource ?? creative.singleVideo?.source}
          poster={creative.singleVideo?.thumbnail ?? creative.thumbnailURL}
        />
      )}
      {(isImage || isVideo) && <LinkStrip creative={creative} />}
      {!isImage && !isVideo && <Placeholder creative={creative} />}
    </div>
  )
}
